import { GenericObjectType, RJSFSchema } from "@rjsf/utils";

import { FormDataForSchema, combineObjects, isPlainObject } from "@nens/shared-utils";

import { ObjectSchema } from "../types/object-schema";

import { isObjectSchema } from "./is-object-schema";

const getRootDefaults = (schema: RJSFSchema): unknown => {
  const firstAnyOfOrOneOfSchema = (schema.anyOf ?? schema.oneOf)?.find(
    (entry): entry is RJSFSchema => typeof entry !== "boolean"
  );
  const firstAnyOfOrOneOfDefault = firstAnyOfOrOneOfSchema
    ? getSchemaDefaultValues(firstAnyOfOrOneOfSchema)
    : undefined;

  const enumDefault =
    Array.isArray(schema.enum) && schema.enum.length > 0 ? schema.enum[0] : undefined;

  return schema.const ?? schema.default ?? enumDefault ?? firstAnyOfOrOneOfDefault;
};

const getNestedDefaults = (schema: ObjectSchema): GenericObjectType => {
  const nestedDefaults: GenericObjectType = {};
  Object.entries(schema.properties).forEach(([key, property]) => {
    if (typeof property === "boolean") return;

    const value = getSchemaDefaultValues(property);

    if (value !== undefined) {
      nestedDefaults[key] = value;
    }
  });

  return nestedDefaults;
};

/**
 * Recursively extracts default values from a JSON schema.
 * By their `default` and `const` values. Root defined defaults win over nested defaults. Example:
 * ```json
 * {
 *   "type": "object",
 *   "properties": {
 *     "a": {
 *       "type": "object",
 *       "properties": {
 *         "b": {
 *           "type": "string",
 *           "default": "nested"
 *         },
 *         "c": {
 *           "type": "string",
 *           "default": "nested"
 *         }
 *       },
 *       "default": {
 *         "b": "root"
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * Will return:
 *
 * ```json
 * {
 *   "a": {
 *     "b": "root",
 *     "c": "nested"
 *   }
 * }
 * ```
 */
export function getSchemaDefaultValues<Schema extends RJSFSchema | undefined>(
  schema: Schema
): FormDataForSchema<Schema>;
export function getSchemaDefaultValues(schema: RJSFSchema): unknown {
  if (!schema) return;

  const rootDefaults = getRootDefaults(schema);

  // For objects schema's we need to get all the defaults from the nested properties as well, and combine them with the root defaults.
  if (isObjectSchema(schema)) {
    const nestedDefaults = getNestedDefaults(schema);

    // Use combine objects so that root defaults win over nested defaults.
    // But any defaults defined in the nested properties will still be included if they have not been defined in the root defaults.
    const combinedDefaults = combineObjects(
      nestedDefaults,
      isPlainObject(rootDefaults) ? rootDefaults : {}
    );

    if (Object.keys(nestedDefaults).length) {
      return combinedDefaults;
    }
  }

  return rootDefaults;
}
